<script setup>
// Renders the template the way WhatsApp will, so the client sees what Meta will review.
// Variables show their example value when there is one, otherwise the raw placeholder.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { isAuthentication, variablesIn } from '../helpers';

const props = defineProps({
  form: { type: Object, required: true },
});

const { t } = useI18n();

const fill = (text, examples) => {
  const found = variablesIn(text);
  return found.reduce(
    (acc, variable, index) =>
      acc.replace(
        new RegExp(`\\{\\{\\s*${variable}\\s*\\}\\}`),
        examples[index] || `{{${variable}}}`
      ),
    text
  );
};

// Meta writes the authentication copy itself, so the preview shows what it will render.
const isAuth = computed(() => isAuthentication(props.form));
const authBody = computed(() =>
  [
    // vue-i18n reads `{{1}}` as a nested placeholder and throws, so the sample code is a param.
    t('WASAPI_TEMPLATES.PREVIEW.AUTH_CODE', { code: '123456' }),
    props.form.securityRecommendation
      ? t('WASAPI_TEMPLATES.PREVIEW.AUTH_SECURITY')
      : null,
  ]
    .filter(Boolean)
    .join(' ')
);
const headerText = computed(() =>
  fill(props.form.headerText, [props.form.headerExample])
);
const bodyText = computed(() => fill(props.form.body, props.form.bodyExamples));
const mediaIcon = computed(
  () =>
    ({
      IMAGE: 'i-lucide-image',
      VIDEO: 'i-lucide-video',
      DOCUMENT: 'i-lucide-file-text',
    })[props.form.headerFormat]
);
const buttonIcon = type =>
  ({
    URL: 'i-lucide-external-link',
    PHONE_NUMBER: 'i-lucide-phone',
    COPY_CODE: 'i-lucide-copy',
  })[type] || 'i-lucide-reply';
</script>

<template>
  <div class="flex flex-col gap-2">
    <span class="text-xs font-medium text-n-slate-11">
      {{ t('WASAPI_TEMPLATES.DIALOG.PREVIEW') }}
    </span>
    <div class="p-3 rounded-xl bg-n-alpha-1">
      <div
        class="flex flex-col gap-2 p-3 overflow-hidden text-sm shadow-sm rounded-xl bg-n-solid-1 text-n-slate-12"
      >
        <div
          v-if="mediaIcon"
          class="grid h-24 rounded-lg bg-n-alpha-2 place-content-center text-n-slate-10"
        >
          <Icon :icon="mediaIcon" class="size-8" />
        </div>
        <p
          v-if="form.headerFormat === 'TEXT' && headerText"
          class="font-semibold"
        >
          {{ headerText }}
        </p>
        <p class="whitespace-pre-wrap">{{ isAuth ? authBody : bodyText }}</p>
        <p v-if="isAuth" class="text-xs text-n-slate-10">
          {{
            t('WASAPI_TEMPLATES.PREVIEW.AUTH_EXPIRATION', {
              minutes: form.codeExpirationMinutes,
            })
          }}
        </p>
        <p v-else-if="form.footer" class="text-xs text-n-slate-10">
          {{ form.footer }}
        </p>
      </div>
      <div
        v-if="isAuth"
        class="flex items-center justify-center gap-1.5 py-2 mt-1 text-sm rounded-lg shadow-sm bg-n-solid-1 text-n-blue-11"
      >
        <Icon icon="i-lucide-copy" class="size-3.5" />
        {{ form.otpButtonText }}
      </div>
      <div v-else-if="form.buttons.length" class="flex flex-col gap-1 mt-1">
        <div
          v-for="(button, index) in form.buttons"
          :key="index"
          class="flex items-center justify-center gap-1.5 py-2 text-sm rounded-lg shadow-sm bg-n-solid-1 text-n-blue-11"
        >
          <Icon :icon="buttonIcon(button.type)" class="size-3.5" />
          {{ button.text || t(`WASAPI_TEMPLATES.TYPES.${button.type}`) }}
        </div>
      </div>
    </div>
  </div>
</template>
